/**
 * `usePartyTransactions` — live read of all transactions for a party
 * across every project in the org. Powers the Party Detail → Ledger
 * tab.
 *
 * Mirrors the read shape of `useProjectTransactions` so callers can
 * reuse the same TransactionItem type + ledger helpers
 * (`txDateMs`, `normaliseTxType`, `isCountedInTotals`).
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';
import {
  txDateMs,
  type TransactionItem,
} from '@/lib/useProjectTransactions';

export type UsePartyTransactionsResult = {
  transactions: TransactionItem[];
  loading: boolean;
  error: string | null;
};

export function usePartyTransactions(
  orgId: string | null | undefined,
  partyId: string | null | undefined,
): UsePartyTransactionsResult {
  const [items, setItems] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !partyId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      query(
        collection(db, 'transactions'),
        where('orgId', '==', orgId),
        where('partyId', '==', partyId),
      ),
      (snap) => {
        setItems(snap.docs.map((d) => ({ ...(d.data() as TransactionItem), id: d.id })));
        setLoading(false);
      },
      (err) => {
        console.warn('[usePartyTransactions] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [orgId, partyId]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => txDateMs(b) - txDateMs(a)),
    [items],
  );

  return { transactions: sorted, loading, error };
}
